import { Row, Col, Card, Badge, Form, Stack } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { store } from "Store";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useReducer } from "react";
import { FormTextInput } from "Components/Forms/FormInput";
import { generateDSSRpt, loadDSSFormInfo, validateDSSRpt } from "./DSSummaryReportAxiosFuncs";
import { formInfoReducer, ACTIONS } from "./DSSummaryReportFormDataReducer";
import ErrorIcon from "@mui/icons-material/ErrorOutlined";
import { IconTextButton, TextButton } from "Components/Button";
import { ERROR_MESSAGES } from "Utils/constants";
import { setGlobalErrorStatus } from "Components/StatusHandling";
import { applyDateSelectorFormat } from "Utils/utilityFunctions";
import { ICONS } from "Utils/icons";

const nameConverter = {
  reportType: "reportType",
  startDate: "startDate",
  endDate: "endDate",
  reportFileName: "fileName"
};

const DSSummaryReport = () => {
  const loginState = store.getState().loginInfo;
  const token = "Bearer " + loginState.token;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initialState = {
    data: "",
    loading: true,
    error: null
  };
  const [formInfo, formInfoDispatch] = useReducer(formInfoReducer, initialState);

  const formFieldDisplay = {
    reportType: {
      type: "radio",
      id: "reportType",
      name: "reportType",
      display: {
        label: "Report Type",
        description: "",
        title: ""
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    },
    startDate: {
      type: "date",
      id: "startDate",
      name: "startDate",
      display: {
        label: "Start Date",
        description: "",
        title: ""
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    },
    endDate: {
      type: "date",
      id: "endDate",
      name: "endDate",
      display: {
        label: "End Date",
        description: "",
        title: ""
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    },
    fileName: {
      type: "text",
      id: "fileName",
      name: "fileName",
      display: {
        label: "Report File Name",
        description: (
          <>
            Report Name should not exceed 100 characters in length. <br /> Report
            Name should not have improper characters \ / : & * ? " &lt; &gt; | .
          </>
        ),
        title: ""
      },
      hideFieldOrgTypes: [],
      InputRef: useRef(null)
    }
  };

  const handleRadioChange = (e) => {
    formInfoDispatch({
      type: ACTIONS.RADIO_CHANGE,
      value: e.target.value,
      name: e.target.name
    });
  };

  const handleDateChange = (e) => {
    const val = applyDateSelectorFormat(e.target.value);
    formInfoDispatch({
      type: ACTIONS.DATE_CHANGE,
      value: val,
      name: e.target.name
    });
  };

  const handleTextChange = (e) => {
    formInfoDispatch({
      type: ACTIONS.TEXT_CHANGE,
      value: e.target.value,
      name: e.target.name
    });
  };

  const dispErrors = (fieldName) => {
    const data = formInfo.data[fieldName];
    const dispData = formFieldDisplay[fieldName];
    return (
      <div>
        {data.validation.errors.length > 0
          ? data.validation.errors.map((errorText, index) => (
            <Stack
              key={dispData.name + "Errors_" + index}
              direction="horizontal"
              gap={2}
              className="align-items-start"
            >
              <div id="errorIcon">
                <ErrorIcon fontSize="small" />
              </div>
              <div>{errorText}</div>
            </Stack>
          ))
          : null}
      </div>
    );
  };

  const dispLabel = (fieldName, isHidden, getColLabelSizes, alignment, multi) => {
    const data = formInfo.data[fieldName];
    const dispData = formFieldDisplay[fieldName];

    if (alignment == Row) {
      if (multi == true) {
        return (
          <Col
            hidden={isHidden}
            xs={getColLabelSizes("xs")}
            sm={getColLabelSizes("sm")}
            md={getColLabelSizes("md")}
            lg={getColLabelSizes("lg")}
            xl={getColLabelSizes("xl")}
            xxl={getColLabelSizes("xxl")}
          >
            <legend>
              <span className="me-2">{dispData.display.label}</span>
              {data.validation.required ? "" : <Badge bg="optional">Optional</Badge>}
            </legend>
          </Col>
        );
      } else {
        return (
          <Form.Label
            visuallyHidden={isHidden}
            column
            xs={getColLabelSizes("xs")}
            sm={getColLabelSizes("sm")}
            md={getColLabelSizes("md")}
            lg={getColLabelSizes("lg")}
            xl={getColLabelSizes("xl")}
            xxl={getColLabelSizes("xxl")}
          >
            <span className="me-2">{dispData.display.label}</span>
            {data.validation.required ? "" : <Badge bg="optional">Optional</Badge>}
          </Form.Label>
        );
      }
    } else {
      if (multi == true) {
        return (
          <Row
            hidden={isHidden}
            xs={getColLabelSizes("xs")}
            sm={getColLabelSizes("sm")}
            md={getColLabelSizes("md")}
            lg={getColLabelSizes("lg")}
            xl={getColLabelSizes("xl")}
            xxl={getColLabelSizes("xxl")}
          >
            <legend>
              {dispData.display.label}
              {data.validation.required ? "" : <Badge bg="optional">Optional</Badge>}
            </legend>
          </Row>
        );
      } else {
        return (
          <Form.Label
            visuallyHidden={isHidden}
            xs={getColLabelSizes("xs")}
            sm={getColLabelSizes("sm")}
            md={getColLabelSizes("md")}
            lg={getColLabelSizes("lg")}
            xl={getColLabelSizes("xl")}
            xxl={getColLabelSizes("xxl")}
          >
            {dispData.display.label}
            {data.validation.required ? "" : <Badge bg="optional">Optional</Badge>}
          </Form.Label>
        );
      }
    }
  };

  function getFieldValue(fieldName, data) {
    const values = data[fieldName];
    const dispValues = formFieldDisplay[fieldName];

    if (formInfo.loading) {
      return "";
    } else {
      const type = dispValues.type;
      switch (type) {
        case "text":
          return values.value ?? "";
        case "date":
          if (values.value != null) {
            const split = String(values.value).split("-");
            if (split.length === 3) {
              return split[1] + "/" + split[2] + "/" + split[0];
            }
            return values.value;
          }
          return "";
        case "radio":
          if (values?.radioButtons) {
            const sel = values.radioButtons.find(b => b.isChecked);
            return sel ? sel.value : null;
          }
          return null;
        default:
          return "";
      }
    }
  }

  function addErrorMessages(fieldName, errorFieldName, data, isFirst) {
    const d = data.fieldErrors;
    const values = d?.[errorFieldName];

    if (values != null) {
      const field = formFieldDisplay[fieldName];
      const type = field.type;
      const err = values.errorMessages;

      if (isFirst === true) {
        field.InputRef.current?.focus();
      }

      switch (type) {
        case "text":
          formInfoDispatch({
            type: ACTIONS.TEXT_SET_ERRORS,
            name: fieldName,
            errors: err
          });
          break;
        case "date":
          formInfoDispatch({
            type: ACTIONS.TEXT_SET_ERRORS,
            name: fieldName,
            errors: err
          });
          break;
        case "radio":
          formInfoDispatch({
            type: ACTIONS.RADIO_SET_ERRORS,
            name: fieldName,
            errors: err
          });
          break;
        default:
          break;
      }
    }
  }

  function clearErrorMessages(data) {
    for (const key of Object.keys(data)) {
      const t = formFieldDisplay[key];
      switch (t.type) {
        case "text":
          formInfoDispatch({
            type: ACTIONS.TEXT_REMOVE_ERRORS,
            name: key
          });
          break;
        case "date":
          formInfoDispatch({
            type: ACTIONS.TEXT_REMOVE_ERRORS,
            name: key
          });
          break;
        case "radio":
          formInfoDispatch({
            type: ACTIONS.RADIO_REMOVE_ERRORS,
            name: key
          });
          break;
        default:
          break;
      }
    }
  }

  const handleCancel = () => {
    navigate("/dashboard");
  };

  useEffect(() => {
    loadDSSFormInfo(
      ACTIONS,
      formInfoDispatch,
      handleRadioChange,
      handleDateChange,
      handleTextChange,
      token
    ).catch((error) => {
      const payload = error?.data ?? error;
      setGlobalErrorStatus({
        dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: payload?.data?.globalErrorMessageList?.[0] ?? ERROR_MESSAGES.DATA_ERROR.message,
          showContactHelp: true
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        },
        error: error
      });
    });
  }, []);

  const handleGenerate = async () => {
    const validation = await validateDSSRpt(
      token,
      formInfo,
      nameConverter,
      addErrorMessages,
      clearErrorMessages,
      getFieldValue
    ).catch((error) => {
      setGlobalErrorStatus({
        dispatch: dispatch,
        popupData: {
          visible: true,
          title: ERROR_MESSAGES.DATA_ERROR.title,
          msg: ERROR_MESSAGES.DATA_ERROR.message,
          showContactHelp: true
        },
        redirect: {
          redirect: false,
          redirectFirst: false,
          path: "",
          data: {}
        },
        error: error
      });
    });

    if (validation?.data?.status === 1) {
      const res = await generateDSSRpt(
        formInfo,
        "Bearer " + loginState.token,
        getFieldValue
      ).catch((error) => {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
      });

      if (res?.status?.code === 1) {
        const fileName =
          res.data.headers["content-disposition"]
            ?.split("filename=")[1]
            .split(";")[0]
            .replace(/^"(.*)"$/, "$1") || "data-submission-summary-report.csv";

        const url = window.URL.createObjectURL(res.data.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: res?.data?.status?.text
        });
      }
    }
  };

  return (
    <Row>
      {formInfo.loading ? null : (
        <Col xl={11} lg={11} md={11} sm={12}>
          <Card className="p-4">
            <Card.Body className="text-start">
              <h1 id="mainContent" className="header1 text-start mb-3 blue bold">
                Data Submission Summary Report
              </h1>
              <p>
                The Year and Month Summary Report shows how many patient safety reports were accepted by PSO, Common
                Formats version, month, and year within your selected date range.
              </p>
              <p>
                The Summary Report by Common Formats Version and Event Type provides patient safety data submission
                variances between snapshots captured within the specified date range. Snapshots have been captured
                twice monthly - on the 1st and 15th - since November 2018.
              </p>
              <p> Answer the questions below to create your report. </p>
              <br />
              <Form>
                <FormTextInput
                  orgType={"Admin"}
                  fieldName="reportType"
                  hideLabel={false}
                  inline={false}
                  horizontalForm={{
                    is: true,
                    labelCols: { lg: 3, md: 4 },
                    inputCols: { lg: 9 }
                  }}
                  displayLabelFunc={dispLabel}
                  data={formInfo.data}
                  fieldData={formFieldDisplay}
                  displayErrorsFunc={dispErrors}
                />
                <FormTextInput
                  orgType={"Admin"}
                  fieldName="startDate"
                  hideLabel={false}
                  horizontalForm={{
                    is: true,
                    labelCols: { lg: 3, md: 4 },
                    inputCols: { lg: 3 }
                  }}
                  displayLabelFunc={dispLabel}
                  data={formInfo.data}
                  fieldData={formFieldDisplay}
                  displayErrorsFunc={dispErrors}
                />
                <FormTextInput
                  orgType={"Admin"}
                  fieldName="endDate"
                  hideLabel={false}
                  horizontalForm={{
                    is: true,
                    labelCols: { lg: 3, md: 4 },
                    inputCols: { lg: 3 }
                  }}
                  displayLabelFunc={dispLabel}
                  data={formInfo.data}
                  fieldData={formFieldDisplay}
                  displayErrorsFunc={dispErrors}
                />
                <FormTextInput
                  orgType={"Admin"}
                  fieldName="fileName"
                  hideLabel={false}
                  horizontalForm={{
                    is: true,
                    labelCols: { lg: 3, md: 4 },
                    inputCols: { lg: 7 }
                  }}
                  displayLabelFunc={dispLabel}
                  data={formInfo.data}
                  fieldData={formFieldDisplay}
                  displayErrorsFunc={dispErrors}
                />
                <br />
                <Col className="d-flex gap-4">
                  <IconTextButton
                    className="reverse"
                    icon={ICONS.download}
                    onClick={handleGenerate}
                    variant="normal"
                    content={"Generate Report"}
                  />
                  <TextButton
                    variant="outline"
                    onClick={handleCancel}
                    content={"Cancel"}
                  />
                </Col>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default DSSummaryReport;
